import React, {useEffect, useRef, useState} from 'react';
import {TextField, TextFieldProps} from '@mui/material';

type Props = Omit<TextFieldProps, 'type' | 'value' | 'onChange'> & {
    value: number | string | null | undefined;
    onValueChange: (value: number | undefined) => void;
};

const toInputValue = (value: number | string | null | undefined) =>
    value === null || value === undefined || (typeof value === 'number' && Number.isNaN(value)) ? '' : String(value);

export const parseLocalizedDecimal = (value: string): number | undefined => {
    if (value.trim() === '') return undefined;
    const parsed = Number(value.replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : undefined;
};

export const LocalizedDecimalField = ({value, onValueChange, inputProps, onFocus, onBlur, ...props}: Props) => {
    const [inputValue, setInputValue] = useState(() => toInputValue(value));
    const focused = useRef(false);

    useEffect(() => {
        if (!focused.current) setInputValue(toInputValue(value));
    }, [value]);

    return <TextField
        {...props}
        type="text"
        value={inputValue}
        inputProps={{...inputProps, inputMode: 'decimal'}}
        onFocus={event => {
            focused.current = true;
            onFocus?.(event);
        }}
        onChange={event => {
            const next = event.target.value;
            if (!/^\d*(?:[.,]\d*)?$/.test(next)) return;
            setInputValue(next);
            if (next === '') onValueChange(undefined);
            else {
                const parsed = parseLocalizedDecimal(next);
                if (parsed !== undefined) onValueChange(parsed);
            }
        }}
        onBlur={event => {
            focused.current = false;
            const parsed = parseLocalizedDecimal(inputValue);
            setInputValue(toInputValue(parsed));
            onValueChange(parsed);
            onBlur?.(event);
        }}
    />;
};
